import * as React from "react";
import { Loader } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function StorageInventoryContent() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="flex h-64 items-center justify-center">
        <Loader className="text-primary size-8 animate-spin" />
        <span className="ml-2 text-lg">Ladataan varaustilannetta...</span>
      </div>;
  }
  const filteredPosts = posts.filter(post => post.customFields?.status === "Vapaa" || post.customFields?.lisatieto && post.customFields.lisatieto.trim() !== "");
  const formatPrice = (price: any) => {
    const num = parseFloat(price);
    if (isNaN(num)) return "0,00 €";
    return `${num.toFixed(2).replace(".", ",")} €`;
  };
  return <>
      <div className="mb-12 overflow-hidden rounded-xl border bg-card p-6 shadow-sm">
        <h3 className="mb-6 font-default text-xl font-bold tracking-tight text-foreground sm:text-2xl">
          Varaston pohjapiirros
        </h3>
        <div className="flex justify-center">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1670724/img/varastot.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1670724/img/varastot.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1670724/img/varastot.png-wvc-sizes" alt="Varaston pohjapiirros" loading="lazy" className="h-auto w-full max-w-4xl rounded-lg object-contain" />
        </div>
      </div>

      <div className="bg-card hidden rounded-xl border shadow-sm md:block">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              <TableHead className="w-[150px] font-semibold">Yksikkö</TableHead>
              <TableHead className="font-semibold">Neliömäärä</TableHead>
              <TableHead className="font-semibold">Hinta</TableHead>
              <TableHead className="font-semibold">Tila</TableHead>
              <TableHead className="font-semibold">Lisätieto</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredPosts.map((post, i) => <TableRow key={post.id} data-index={i} className={i % 2 === 0 ? "bg-background" : "bg-muted/20"}>
                <TableCell className="font-default font-medium">
                  {post.customFields?.unit_number || "N/A"}
                </TableCell>
                <TableCell>
                  {post.customFields?.size ? `${post.customFields.size} m²` : "-"}
                </TableCell>
                <TableCell>{formatPrice(post.customFields?.price)}</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center rounded-lg px-2.5 py-0.5 text-xs font-medium ${post.customFields?.status === "Vapaa" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                    {post.customFields?.status === "Vapaa" ? "Vapaa" : "Varattu"}
                  </span>
                </TableCell>
                <TableCell className="min-w-[200px] py-3">
                  <textarea readOnly className="flex min-h-[60px] w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none italic text-muted-foreground" value={post.customFields?.lisatieto || ""} placeholder="Ei lisätietoja" />
                </TableCell>
              </TableRow>)}
            {filteredPosts.length === 0 && <TableRow>
                <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                  Ei saatavilla olevia yksiköitä.
                </TableCell>
              </TableRow>}
          </TableBody>
        </Table>
      </div>

      <div className="grid grid-cols-1 gap-4 md:hidden">
        {filteredPosts.map((post, i) => <Card key={post.id} data-index={i} className="overflow-hidden">
            <CardHeader className="bg-muted/20 pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="font-default text-xl">
                  {post.customFields?.unit_number || "N/A"}
                </CardTitle>
                {post.customFields?.size && <span className="font-mono text-sm font-medium">{post.customFields.size} m²</span>}
              </div>
            </CardHeader>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between border-b pb-3">
                <span className="text-muted-foreground text-sm">Hinta</span>
                <span className="font-medium">{formatPrice(post.customFields?.price)}</span>
              </div>
              <div className="flex items-center justify-between border-b pt-3 pb-3">
                <span className="text-muted-foreground text-sm">Tila</span>
                <span className={`text-xs font-medium uppercase tracking-wider ${post.customFields?.status === "Vapaa" ? "text-green-600" : "text-red-600"}`}>
                  {post.customFields?.status === "Vapaa" ? "Vapaa" : "Varattu"}
                </span>
              </div>
              <div className="flex flex-col gap-2 pt-3 pb-1">
                <span className="text-muted-foreground text-sm">Lisätieto</span>
                <textarea readOnly className="flex min-h-[80px] w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none italic text-muted-foreground" value={post.customFields?.lisatieto || ""} placeholder="Ei lisätietoja" />
              </div>
            </CardContent>
          </Card>)}
        {filteredPosts.length === 0 && <p className="text-center text-muted-foreground py-8">Ei saatavilla olevia yksiköitä.</p>}
      </div>
    </>;
}
export default function StorageUnitInventory() {
  const wp_query_params = {
    post_type: "varasto",
    posts_per_page: 100,
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="storageunitinventory" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 text-center">
          <h2 className="font-default text-foreground mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
            Varaustilanne
          </h2>
          <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
            Tutustu Varastomikkelin ajantasaiseen varaustilanneseen. Täältä näet kaikki varastoyksikkömme.
          </p>
        </div>

        <PostsProvider wp_query={{
        post_type: "varasto",
        status: ["publish"],
        per_page: 100
      }}>
          <StorageInventoryContent />
        </PostsProvider>
      </div>
    </section>;
}